import { Component } from 'react'

// 使用 sessionStorage 记录连续错误次数，防止无限刷新循环
const MAX_RETRIES = 3
const RETRY_KEY = 'edu_error_retry_count'

function getRetryCount() {
  try {
    return parseInt(sessionStorage.getItem(RETRY_KEY) || '0', 10)
  } catch {
    return 0
  }
}

function setRetryCount(count) {
  try {
    sessionStorage.setItem(RETRY_KEY, String(count))
  } catch {
    // ignore
  }
}

function resetRetryCount() {
  try {
    sessionStorage.removeItem(RETRY_KEY)
  } catch {
    // ignore
  }
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({ errorInfo: errorInfo.componentStack })
    // 记录错误次数
    const count = getRetryCount()
    setRetryCount(count + 1)
  }

  handleReset = () => {
    const count = getRetryCount()
    if (count >= MAX_RETRIES) {
      // 连续错误次数过多，显示恢复指引而非继续刷新
      console.warn('ErrorBoundary: max retries reached, showing recovery UI')
      this.setState({ showRecovery: true })
      return
    }
    this.setState({ hasError: false, error: null, errorInfo: null, showRecovery: false })
    window.location.reload()
  }

  handleClearData = () => {
    // 清除所有本地存储数据，强制回到初始状态
    localStorage.clear()
    sessionStorage.clear()
    resetRetryCount()
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      // 连续错误过多，显示恢复指引
      if (this.state.showRecovery || getRetryCount() >= MAX_RETRIES) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-lg">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                <i className="fa-solid fa-wrench text-2xl text-orange-500" />
              </div>
              <h2 className="text-lg font-bold text-gray-800 mb-2">应用遇到了问题</h2>
              <p className="text-sm text-gray-500 mb-4">
                页面反复出错，可能需要清除缓存数据来恢复
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={this.handleClearData}
                  className="w-full py-3 rounded-xl font-medium text-white transition-colors"
                  style={{ background: '#FF6B6B' }}
                >
                  清除数据并重新开始
                </button>
                <button
                  onClick={resetRetryCount}
                  className="w-full py-3 rounded-xl font-medium text-gray-600 transition-colors bg-gray-100"
                >
                  稍后再试
                </button>
              </div>
            </div>
          </div>
        )
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-lg">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <i className="fa-solid fa-triangle-exclamation text-2xl text-red-500" />
            </div>
            <h2 className="text-lg font-bold text-gray-800 mb-2">出错了</h2>
            <p className="text-sm text-gray-500 mb-2">
              {this.state.error?.message || '应用遇到了一些问题'}
            </p>
            {this.state.errorInfo && (
              <pre className="text-xs text-left bg-gray-100 p-2 rounded mb-4 overflow-auto max-h-40">
                {this.state.errorInfo}
              </pre>
            )}
            <button
              onClick={this.handleReset}
              className="w-full py-3 rounded-xl font-medium text-white transition-colors"
              style={{ background: '#6C63FF' }}
            >
              刷新页面
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
